import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconGolf, IconTrophy, IconCrown, IconThumbUp, IconThumbDown,
  IconArrowsMinimize, IconArrowsMaximize,
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
} from '@tabler/icons-react';
import api from '../api/api';

// ── 유틸 ──
const WEATHER_ICONS = {
  sunny:  { Icon: IconSun,        color: '#f59e0b' },
  cloudy: { Icon: IconCloud,      color: '#6b7280' },
  rain:   { Icon: IconCloudRain,  color: '#3b82f6' },
  snow:   { Icon: IconSnowflake,  color: '#60a5fa' },
  wind:   { Icon: IconWind,       color: '#8b5cf6' },
  storm:  { Icon: IconCloudStorm, color: '#ef4444' },
};
const WeatherBadge = ({ code, temperature }) => {
  const w = WEATHER_ICONS[code];
  if (!w) return null;
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <w.Icon size={13} color={w.color} />
      {temperature && <span style={{ fontSize: 11, opacity: 0.7 }}>{temperature}</span>}
    </span>
  );
};

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const formatDateWithDay = (dateStr) => {
  if (!dateStr) return { text: '-', day: '', dayColor: undefined };
  const d = new Date(dateStr);
  const day = DAY_NAMES[d.getDay()];
  const dayColor = d.getDay() === 0 ? '#e03131' : d.getDay() === 6 ? '#206bc4' : undefined;
  return { text: dateStr, day, dayColor };
};

const HoleBadge = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  const h0 = sections[0]?.holesCount || 9;
  if (sections.length === 1) return <span className="badge bg-azure-lt" style={{ fontSize: 10 }}>{h0}홀</span>;
  const h1 = sections[1]?.holesCount || 9;
  if (sections[0]?.sectionId === sections[1]?.sectionId)
    return <span className="badge bg-purple-lt" style={{ fontSize: 10 }}>{h0}x2</span>;
  return <span className="badge bg-green-lt" style={{ fontSize: 10 }}>{h0 + h1}홀</span>;
};

// ── 메인 컴포넌트 ──
export default function SettlementPage({ me, showToast }) {
  const navigate = useNavigate();
  const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
  const mc = (m) => (isDark ? m.darkColor : m.color) || m.color || undefined;

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [members, setMembers] = useState([]);
  const currentYear = new Date().getFullYear();
  const [compact, setCompact] = useState(() => sessionStorage.getItem('settle_compact') === 'true');

  // 필터 (sessionStorage로 유지)
  const [year, setYear] = useState(() => sessionStorage.getItem('settle_year') ?? String(currentYear));
  const [filterGameType, setFilterGameType] = useState(() => sessionStorage.getItem('settle_gameType') ?? '');

  useEffect(() => { sessionStorage.setItem('settle_year', year); }, [year]);
  useEffect(() => { sessionStorage.setItem('settle_gameType', filterGameType); }, [filterGameType]);
  useEffect(() => { sessionStorage.setItem('settle_compact', String(compact)); }, [compact]);
  const yearOptions = Array.from({ length: currentYear - 2009 }, (_, i) => currentYear - i);


  useEffect(() => {
    api.get('/golf/members').then(res => setMembers(res.data || [])).catch(() => {});
  }, []);

  // 데이터 로드: 유효(useYn=Y)만
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        sortField: 'reservedDate', sortDir: 'desc', includeScores: true,
        size: 9999, useYn: 'Y',
      };
      if (year) { params.dateFrom = `${year}-01-01`; params.dateTo = `${year}-12-31`; }
      if (filterGameType) params.gameType = filterGameType;

      const res = await api.get('/golf/reservations', { params });
      const data = res.data.content || [];
      setReservations(data);
      setTotalElements(res.data.totalElements || 0);
    } catch {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [year, filterGameType]);

  useEffect(() => { loadData(); }, [loadData]);

  // 멤버별 통계 (gameCount 없음 — 전체 유효 대상)
  const memberStats = useMemo(() => {
    const perUser = {};
    reservations.forEach(r => {
      if (!r.scores) return;
      r.scores.forEach(s => {
        if (!s.userId || s.joinYn === 'N' || !s.totalScore || s.totalScore <= 0) return;
        if (!perUser[s.userId]) perUser[s.userId] = [];
        perUser[s.userId].push({ score: s.totalScore, holeScoresJson: s.holeScoresJson });
      });
    });

    return Object.entries(perUser).map(([uid, games]) => {
      let eagle = 0, birdie = 0, par = 0, bogey = 0;
      const scores = games.map(g => g.score);
      games.forEach(g => {
        if (g.holeScoresJson) {
          try {
            const json = typeof g.holeScoresJson === 'string' ? JSON.parse(g.holeScoresJson) : g.holeScoresJson;
            Object.keys(json).forEach(key => {
              if (key.startsWith('putting')) return;
              const holes = json[key]; if (!holes) return;
              Object.values(holes).forEach(val => {
                const v = Number(val) || 0;
                if (v <= -2) eagle++; else if (v === -1) birdie++;
                else if (v === 0) par++; else if (v >= 2) bogey++;
              });
            });
          } catch {}
        }
      });

      return {
        userId: Number(uid), playCount: scores.length,
        avgScore: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10 : 0,
        bestScore: scores.length > 0 ? Math.min(...scores) : 0,
        worstScore: scores.length > 0 ? Math.max(...scores) : 0,
        eagle, birdie, par, bogey,
      };
    });
  }, [reservations]);

  const findScoreForMember = (reservation, memberId) => {
    if (!reservation.scores) return null;
    return reservation.scores.find(s => s.userId === memberId && s.joinYn !== 'N');
  };

  const openScoreCard = (r) => {
    navigate(`/golf/reservations/${r.id}/scorecard`, { state: { reservation: r } });
  };

  return (
    <>
      {/* ── 검색 필터 ── */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <IconTrophy size={18} className="text-warning" />
            <h3 className="card-title mb-0">결산</h3>
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 80 }}
              value={year} onChange={e => setYear(e.target.value)}>
              {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 80 }}
              value={filterGameType} onChange={e => setFilterGameType(e.target.value)}>
              <option value="">전체</option>
              <option value="일반">일반</option>
              <option value="메이저">메이저</option>
            </select>
            <span className="badge bg-blue-lt">{totalElements}건</span>
          </div>
        </div>
      </div>

      {/* ── 개인별 통계 ── */}
      {members.length > 0 && (
        <div className="card mb-3">
          <div className="card-body py-3">
            {(() => {
              const statsById = {};
              memberStats.forEach(s => { statsById[s.userId] = s; });
              let maxEagle = 0, maxBirdie = 0, maxPar = 0, maxBogey = 0;
              memberStats.forEach(s => {
                if (s.eagle > maxEagle) maxEagle = s.eagle;
                if (s.birdie > maxBirdie) maxBirdie = s.birdie;
                if (s.par > maxPar) maxPar = s.par;
                if (s.bogey > maxBogey) maxBogey = s.bogey;
              });

              return (
                <div className="d-flex member-stats-wrap" style={{ gap: 4, flexWrap: 'nowrap', justifyContent: 'center' }}>
                  {members.map(m => {
                    const st = statsById[m.id] || {};
                    const hasData = st.playCount > 0;
                    return (
                      <div key={m.id} className="member-stat-item" style={{ flex: '1 1 0', minWidth: 0, maxWidth: 140 }}>
                        <div className="text-center">
                          <div className="member-stat-crown" style={{ fontSize: 9, lineHeight: 1.4, minHeight: 28, marginBottom: 1 }}>
                            {hasData && st.eagle > 0 && st.eagle === maxEagle && (
                              <div style={{ color: '#ab9046', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 이글왕({st.eagle})</div>
                            )}
                            {hasData && st.birdie > 0 && st.birdie === maxBirdie && (
                              <div style={{ color: '#c41000', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 버디왕({st.birdie})</div>
                            )}
                            {hasData && st.par > 0 && st.par === maxPar && (
                              <div style={{ color: '#078f00', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 파왕({st.par})</div>
                            )}
                            {hasData && st.bogey > 0 && st.bogey === maxBogey && (
                              <div style={{ color: '#00078f', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 양파왕({st.bogey})</div>
                            )}
                            {hasData && !(
                              (st.eagle > 0 && st.eagle === maxEagle) || (st.birdie > 0 && st.birdie === maxBirdie) ||
                              (st.par > 0 && st.par === maxPar) || (st.bogey > 0 && st.bogey === maxBogey)
                            ) && (
                              <div className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 9 }}>
                                {st.birdie > 0 ? `버디(${st.birdie})` : ''} {st.par > 0 ? `파(${st.par})` : ''}
                              </div>
                            )}
                          </div>

                          {m.profileImage ? (
                            <img src={m.profileImage} alt={m.nickname || m.name} className="member-stat-avatar" style={{
                              width: 52, height: 52, borderRadius: '50%', margin: '0 auto 4px',
                              objectFit: 'cover', display: 'block', maxWidth: '100%',
                              border: `2.5px solid ${m.color || 'var(--tblr-primary)'}`,
                            }} />
                          ) : (
                            <div className="member-stat-avatar" style={{
                              width: 52, height: 52, borderRadius: '50%', margin: '0 auto 4px',
                              background: m.bgcolor || m.color || 'var(--tblr-primary)',
                              color: m.bgcolor ? (m.color || '#fff') : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 18, fontWeight: 700, maxWidth: '100%',
                              border: `2.5px solid ${m.color || 'var(--tblr-primary)'}`,
                            }}>
                              <span className="member-stat-avatar-text">{(m.nickname || m.name)?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                          )}

                          <div className="member-stat-name" style={{ fontWeight: 700, fontSize: 11, color: mc(m), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.nickname || m.name}
                          </div>
                          {hasData ? (
                            <>
                              <div className="d-flex justify-content-center member-stat-best gap-1 mt-1" style={{ fontSize: 9 }}>
                                <span className="d-inline-flex align-items-center gap-1" style={{ color: '#e03131' }}>
                                  <span className="d-none d-md-inline"><IconThumbUp size={9} /></span> {st.bestScore}
                                </span>
                                <span className="d-inline-flex align-items-center gap-1" style={{ color: '#1971c2' }}>
                                  <span className="d-none d-md-inline"><IconThumbDown size={9} /></span> {st.worstScore}
                                </span>
                              </div>
                              <div className="member-stat-score" style={{ fontSize: 10, fontWeight: 700, marginTop: 1, whiteSpace: 'nowrap', color: mc(m) }}>
                                {Math.round(st.avgScore)}타 / {st.playCount}회
                              </div>
                            </>
                          ) : (
                            <div className="text-muted small mt-1">기록 없음</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── 골프 예약 목록 ── */}
      <div className="card">
        <div className="card-header d-flex align-items-center gap-2 py-2">
          <IconGolf size={18} />
          <h3 className="card-title mb-0">예약 목록</h3>
          <span className="badge bg-blue-lt">{totalElements}건</span>
          <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center" title={compact ? '확대' : '축소'}
            onClick={() => setCompact(v => !v)} style={{ padding: '2px 4px' }}>
            {compact ? <IconArrowsMaximize size={16} /> : <IconArrowsMinimize size={16} />}
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table" style={{ fontSize: compact ? 12 : 13 }}>
            <thead>
              {compact ? (
              <tr>
                <th style={{ padding: '4px 6px' }}>날짜</th>
                <th style={{ padding: '4px 6px' }}>골프장</th>
                {members.map(m => (
                  <th key={m.id} style={{ padding: '4px 4px', textAlign: 'center', color: mc(m), fontSize: 11, maxWidth: 50 }}>
                    {(m.nickname || m.name)?.slice(0, 2)}
                  </th>
                ))}
              </tr>
              ) : (
              <tr>
                <th style={{ width: 50, textAlign: 'center' }}>No</th>
                <th style={{ width: 120 }}>날짜</th>
                <th style={{ width: 60, textAlign: 'center' }}>티업</th>
                <th>골프장</th>
                {members.map(m => (
                  <th key={m.id} style={{ width: 70, textAlign: 'center', color: mc(m) }}>
                    {m.nickname || m.name}
                  </th>
                ))}
              </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={(compact ? 2 : 4) + members.length} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                </td></tr>
              ) : reservations.length === 0 ? (
                <tr><td colSpan={(compact ? 2 : 4) + members.length} className="text-center py-4 text-muted">예약 내역이 없습니다.</td></tr>
              ) : reservations.map((r, idx) => {
                const dateInfo = formatDateWithDay(r.reservedDate);
                const courseSections = r.sections?.map(s => s.sectionName).join(' > ') || '';

                return compact ? (
                  <tr key={r.id}>
                    <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: 11 }}
                      onClick={() => openScoreCard(r)}>
                      <span style={{ color: dateInfo.dayColor || 'var(--tblr-body-color)' }}>{dateInfo.text}</span>
                      <span style={{ color: dateInfo.dayColor, fontWeight: 600 }}>({dateInfo.day})</span>
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: 11, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.golfCourseName?.slice(0, 4)}
                    </td>
                    {members.map(m => {
                      const score = findScoreForMember(r, m.id);
                      const hasScore = score && score.totalScore != null;
                      return (
                        <td key={m.id} className="text-center" style={{ padding: '3px 2px', fontSize: 11 }}>
                          {hasScore ? (<>
                            <span style={{ fontWeight: 700, color: mc(m) }}>{score.totalScore}</span>
                            {score.totalPutting > 0 && <div style={{ fontSize: 9, color: '#888' }}>P{score.totalPutting}</div>}
                          </>) : <span style={{ color: '#ddd' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ) : (
                  <tr key={r.id}>
                    <td className="text-center text-muted">{totalElements - idx}</td>
                    <td style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => openScoreCard(r)}>
                      <span className="fw-medium" style={{ color: dateInfo.dayColor || 'var(--tblr-body-color)', textDecoration: 'underline', cursor: 'pointer' }}>{dateInfo.text}</span>
                      <span style={{ color: dateInfo.dayColor, fontWeight: 600, marginLeft: 2 }}>({dateInfo.day})</span>
                    </td>
                    <td className="text-center fw-medium">{r.reservedTime?.slice(0, 5) || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className="fw-medium">{r.golfCourseName}</span>
                      {courseSections && <span className="text-muted" style={{ fontSize: 11 }}> ({courseSections})</span>}
                      {r.sections?.length > 0 && <span className="ms-1"><HoleBadge sections={r.sections} /></span>}
                      {r.weather && <span className="ms-1"><WeatherBadge code={r.weather} temperature={r.temperature} /></span>}
                      {r.greenFee != null && <span className="text-muted ms-1" style={{ fontSize: 11 }}>&#8361;{r.greenFee}</span>}
                      {r.gameType && <span className={`badge ms-1 ${r.gameType === '메이저' ? 'bg-purple-lt' : 'bg-azure-lt'}`} style={{ fontSize: 10 }}>{r.gameType}</span>}
                    </td>
                    {members.map(m => {
                      const score = findScoreForMember(r, m.id);
                      const hasScore = score && score.totalScore != null;
                      return (
                        <td key={m.id} className="text-center" style={{ fontSize: 13 }}>
                          {hasScore ? (<>
                            <span style={{ fontWeight: 700, color: mc(m) }}>{score.totalScore}</span>
                            {score.totalPutting > 0 && <div style={{ fontSize: 10, color: '#888' }}>P{score.totalPutting}</div>}
                          </>) : <span style={{ color: '#ddd' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
}
